import { Table, Tag, Button, Space, notification, Radio } from "antd"
import { ContestConfig } from "domains/managingJson/store/types"
import { FC, useEffect, useState } from "react"
import { useStore } from "stores/utils/use-store"
import {
  ContestPeriod,
  getContestPeriods,
  deleteContestPeriod,
  ContestPeriodGame,
  getContestPeriodGames,
  GameChallenge,
  getGameChallenges,
  updateContestPeriodGame,
} from "../../../api"
import { defaultFormat } from "utils/date"
import { Column } from "abstract/types/column.type"
import { EditOutlined, DeleteOutlined, CustomerServiceOutlined } from "@ant-design/icons"
import { showDeleteConfirm, CreateNewElement, RaffleSelector } from "components"
import { PeriodModal } from "./PeriodModal"
import { GamesModal } from "./GamesModal"
import { ChallengesModal } from "./ChallengesModal"

export const PeriodsTab: FC = () => {
  const { dataStore: { managingJsonStore: { selectedElement }, managingJsonStore } } = useStore()
  const contestData = selectedElement.data as ContestConfig
  const contestName = selectedElement.name

  const [periods, setPeriods] = useState<ContestPeriod[]>([])
  const [loading, setLoading] = useState(false)
  const [isPeriodModalVisible, setIsPeriodModalVisible] = useState(false)
  const [periodModalType, setPeriodModalType] = useState<"create" | "edit">("create")
  const [isGamesModalVisible, setIsGamesModalVisible] = useState(false)
  const [isChallengesModalVisible, setIsChallengesModalVisible] = useState(false)
  const [editingPeriod, setEditingPeriod] = useState<ContestPeriod | null>(null)
  const [selectedPeriodForGames, setSelectedPeriodForGames] = useState<ContestPeriod | null>(null)
  const [selectedGameForChallenges, setSelectedGameForChallenges] = useState<ContestPeriodGame | null>(null)
  const [games, setGames] = useState<ContestPeriodGame[]>([])
  const [challenges, setChallenges] = useState<GameChallenge[]>([])
  const [gamesLoading, setGamesLoading] = useState(false)
  const [challengesLoading, setChallengesLoading] = useState(false)

  const [isMappedMode, setIsMappedMode] = useState<boolean>(contestData.isMappedRaffleMode || false)
  const [singleRaffleForAll, setSingleRaffleForAll] = useState<string | undefined>()
  const [allGames, setAllGames] = useState<ContestPeriodGame[]>([])

  // Load all games across all periods to manage single raffle mode
  const loadAllGames = async() => {
    const allGamesList: ContestPeriodGame[] = []
    for (const period of periods) {
      try {
        const periodGames = await getContestPeriodGames(period.id)
        allGamesList.push(...periodGames)
      } catch (error) {
        console.error(`Failed to load games for period ${period.id}:`, error)
      }
    }
    setAllGames(allGamesList)

    // If in single mode, set the single raffle ID from the first game that has one
    if (!isMappedMode) {
      const gameWithRaffle = allGamesList.find(game => game.raffleId)
      if (gameWithRaffle) {
        setSingleRaffleForAll(gameWithRaffle.raffleId)
      }
    }
  }

  const updateRaffleMode = (mapped: boolean) => {
    setIsMappedMode(mapped)
    const contestConfig = managingJsonStore.selectedElement.data as ContestConfig
    contestConfig.isMappedRaffleMode = mapped
  }

  const updateSingleRaffleForAll = (raffleId: string | undefined) => {
    setSingleRaffleForAll(raffleId)
  }

  const applySingleRaffleToAllGames = async() => {
    if (!singleRaffleForAll || isMappedMode) return

    try {
      const updatePromises = allGames.map(game => updateContestPeriodGame(
        game.contestPeriodId,
        game.gameId,
        {
          prizeBuffPoints: game.prizeBuffPoints,
          prizeGiftCard: game.prizeGiftCard,
          prizesCount: game.prizesCount,
          raffleId: singleRaffleForAll,
        },
      ))

      await Promise.all(updatePromises)
      notification.success({
        message: `Applied raffle to ${allGames.length} games`,
        description: `All games now use raffle: ${singleRaffleForAll}`,
      })

      // Reload games to reflect changes
      await loadAllGames()
    } catch (error) {
      console.error("Failed to update games with single raffle ID:", error)
      notification.error({ message: "Failed to apply raffle to all games" })
    }
  }

  const loadPeriods = async() => {
    if (!contestName) return

    setLoading(true)
    try {
      const contestPeriods = await getContestPeriods(contestName)
      setPeriods(contestPeriods)
    } catch (error) {
      console.error("Failed to load contest periods:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadGames = async(contestPeriodId: string) => {
    setGamesLoading(true)
    try {
      const periodGames = await getContestPeriodGames(contestPeriodId)
      setGames(periodGames)
    } catch (error) {
      console.error("Failed to load contest period games:", error)
      notification.error({ message: "Failed to load games" })
    } finally {
      setGamesLoading(false)
    }
  }

  const loadChallenges = async(contestPeriodId: string, gameId: number) => {
    setChallengesLoading(true)
    try {
      const gameChallenges = await getGameChallenges(contestPeriodId, gameId)
      setChallenges(gameChallenges)
    } catch (error) {
      console.error("Failed to load game challenges:", error)
      notification.error({ message: "Failed to load challenges" })
    } finally {
      setChallengesLoading(false)
    }
  }

  useEffect(() => {
    loadPeriods()
  }, [contestName])

  useEffect(() => {
    if (periods.length > 0) {
      loadAllGames()
    }
  }, [periods, isMappedMode])

  const handleDelete = async(period: ContestPeriod) => {
    try {
      await deleteContestPeriod(period.id)
      notification.success({ message: "Contest period deleted successfully" })
      await loadPeriods()
    } catch (error) {
      console.error("Failed to delete contest period:", error)
      notification.error({ message: "Failed to delete contest period" })
    }
  }

  const handleCreate = () => {
    setPeriodModalType("create")
    setEditingPeriod(null)
    setIsPeriodModalVisible(true)
  }

  const handleEdit = (period: ContestPeriod) => {
    setPeriodModalType("edit")
    setEditingPeriod(period)
    setIsPeriodModalVisible(true)
  }

  const handleShowGames = (period: ContestPeriod) => {
    setSelectedPeriodForGames(period)
    setIsGamesModalVisible(true)
    loadGames(period.id)
  }

  const handleShowChallenges = (game: ContestPeriodGame) => {
    setSelectedGameForChallenges(game)
    setIsChallengesModalVisible(true)
    loadChallenges(game.contestPeriodId, game.gameId)
  }

  const onClosePeriodModal = () => {
    setIsPeriodModalVisible(false)
    setEditingPeriod(null)
  }

  const onCloseGamesModal = () => {
    setIsGamesModalVisible(false)
    setSelectedPeriodForGames(null)
    setGames([])
  }

  const onCloseChallengesModal = () => {
    setIsChallengesModalVisible(false)
    setSelectedGameForChallenges(null)
    setChallenges([])
  }

  const getStatusColor = (status: ContestPeriod["status"]) => {
    switch (status) {
    case "ACTIVE":
      return "green"
    case "ENDED":
      return "red"
    case "UPCOMING":
      return "blue"
    default:
      return "default"
    }
  }

  const columns: Column[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {id}
        </div>
      ),
      width: 300,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: ContestPeriod["status"]) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Start Date",
      dataIndex: "startsAt",
      key: "startsAt",
      render: (startsAt: string) => (
        <Tag color="green">{defaultFormat(new Date(startsAt))}</Tag>
      ),
      width: 200,
    },
    {
      title: "End Date",
      dataIndex: "endsAt",
      key: "endsAt",
      render: (endsAt: string) => (
        <Tag color="red">{defaultFormat(new Date(endsAt))}</Tag>
      ),
      width: 200,
    },
    {
      title: "Actions",
      key: "actions",
      dataIndex: "actions",
      render: (_, period: ContestPeriod) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(period)}
          >
            Edit
          </Button>
          <Button
            type="default"
            size="small"
            icon={<CustomerServiceOutlined />}
            onClick={() => handleShowGames(period)}
          >
            Games
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              showDeleteConfirm(`contest period: ${period.id.substring(0, 8)}...`, undefined, () => handleDelete(period))
            }}
          >
            Delete
          </Button>
        </Space>
      ),
      width: 250,
    },
  ]

  return (
    <div>
      {/* Raffle Configuration Section */}
      <div
        style={{
          backgroundColor: "#f5f5f5",
          padding: "16px",
          marginBottom: "24px",
          borderRadius: "6px",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0" }}>Raffle Configuration</h3>

        <div style={{ marginBottom: "16px" }}>
          <Radio.Group
            value={isMappedMode}
            onChange={e => updateRaffleMode(e.target.value)}
          >
            <Radio value={false}>Single Raffle Mode</Radio>
            <Radio value={true}>Mapped Raffle Mode</Radio>
          </Radio.Group>
        </div>

        <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>
          {isMappedMode ? (
            "Each game + wave pair is mapped to its own raffle"
          ) : (
            "All game + wave completions award entries to the same raffle"
          )}
        </div>

        {!isMappedMode && (
          <div style={{ marginTop: "16px" }}>
            <div style={{ marginBottom: "8px", fontWeight: "500" }}>
              Select Raffle for All Games:
            </div>
            <RaffleSelector
              value={singleRaffleForAll}
              onChange={updateSingleRaffleForAll}
              placeholder="Search and select raffle"
              style={{ maxWidth: "400px" }}
            />
            <Button
              type="primary"
              onClick={applySingleRaffleToAllGames}
              style={{ marginTop: "10px" }}
              disabled={!singleRaffleForAll || isMappedMode}
            >
              Apply Selected Raffle to All Games
            </Button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0 }}>Contest Periods</h3>
        <CreateNewElement
          valueText="period"
          onClick={handleCreate}
        />
      </div>

      <Table
        dataSource={periods}
        columns={columns as any}
        rowKey="id"
        loading={loading}
      />

      <PeriodModal
        visible={isPeriodModalVisible}
        onClose={onClosePeriodModal}
        editType={periodModalType}
        contestName={contestName}
        editingPeriod={editingPeriod}
        loading={loading}
        onSuccess={loadPeriods}
      />

      <GamesModal
        visible={isGamesModalVisible}
        onClose={onCloseGamesModal}
        selectedPeriod={selectedPeriodForGames}
        games={games}
        loading={gamesLoading}
        onShowChallenges={handleShowChallenges}
        onRefreshGames={() => selectedPeriodForGames && loadGames(selectedPeriodForGames.id)}
        singleRaffleId={singleRaffleForAll}
      />

      <ChallengesModal
        visible={isChallengesModalVisible}
        onClose={onCloseChallengesModal}
        selectedGame={selectedGameForChallenges}
        challenges={challenges}
        loading={challengesLoading}
        onRefreshChallenges={() => {
          if (selectedGameForChallenges) {
            loadChallenges(selectedGameForChallenges.contestPeriodId, selectedGameForChallenges.gameId)
          }
        }}
      />
    </div>
  )
}
